import { useMemo } from 'react'

/**
 * Volume Type from OpenStack API
 */
export interface VolumeType {
  id: string
  name: string
  is_public: boolean
  description: string
  extra_specs: {
    volume_backend_name?: string
    [key: string]: string | undefined
  }
  qos_specs_id: string | null
  'os-volume-type-access:is_public': boolean
}

/**
 * Volume Backend from params (Blueprint configuration)
 */
export interface VolumeBackend {
  type: string // backend name (e.g., "lvm-vt")
  id: string
}

/**
 * Mapped result containing backend with its assigned volume types
 */
export interface VolumeBackendWithTypes {
  backend: VolumeBackend
  assignedVolumeTypes: VolumeType[]
}

/**
 * Creates a Map of backend names to their assigned volume types.
 * Uses O(n) time complexity for building the map.
 *
 * @param volumeTypes - Array of OpenStack volume types
 * @returns Map where key is backend name and value is array of volume types
 */
export function buildVolumeTypesByBackendMap(volumeTypes: VolumeType[]): Map<string, VolumeType[]> {
  const backendMap = new Map<string, VolumeType[]>()

  for (const volumeType of volumeTypes) {
    const backendName = volumeType.extra_specs?.volume_backend_name
    if (!backendName) continue

    const existing = backendMap.get(backendName)
    if (existing) {
      existing.push(volumeType)
    } else {
      backendMap.set(backendName, [volumeType])
    }
  }

  return backendMap
}

/**
 * Gets assigned volume types for a specific backend.
 * O(1) lookup when using pre-built map.
 *
 * @param backendName - The backend name to look up
 * @param backendMap - Pre-built map from buildVolumeTypesByBackendMap
 * @returns Array of volume types assigned to this backend
 */
export function getAssignedVolumeTypes(
  backendName: string,
  backendMap: Map<string, VolumeType[]>,
): VolumeType[] {
  return backendMap.get(backendName) ?? []
}

/**
 * Hook to get a memoized map of backend names to volume types.
 * Rebuilds only when volumeTypes changes.
 *
 * @param volumeTypes - Array of OpenStack volume types
 * @param loading - Whether volume types are still loading
 * @returns Object with the map and loading state
 */
export function useVolumeTypesByBackend(volumeTypes: VolumeType[], loading: boolean) {
  const backendMap = useMemo(() => {
    if (loading || !volumeTypes?.length) {
      return new Map<string, VolumeType[]>()
    }
    return buildVolumeTypesByBackendMap(volumeTypes)
  }, [volumeTypes, loading])

  return {
    backendMap,
    loading,
    getAssignedTypes: (backendName: string) => getAssignedVolumeTypes(backendName, backendMap),
  }
}
